import i18n from "locales/i18n";

function CustomPaletteProvider(
  bpmnFactory,
  create,
  elementFactory,
  palette,
  handTool,
  globalConnect,
) {
  /**
   * HAND TOOL
   */
  function activateHandTool(event) {
    handTool.activateHand(event);
  }

  /**
   * GLOBAL CONNECT TOOL
   */
  function activateGlobalConnect(event) {
    globalConnect.start(event);
  }

  /**
   * START EVENT
   */
  function createStartEvent(event) {
    const businessObject = bpmnFactory.create("bpmn:StartEvent");

    const shape = elementFactory.createShape({
      type: "bpmn:StartEvent",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * Creates a function to start the creation of a BPMN User Task with a given suitability score.
   * @param {number} suitabilityScore - The suitability score to be assigned to the User Task.
   * @returns {Function} - A function that takes an event and starts the creation of a User Task.
   */
  function createUserTask(event) {
    const businessObject = bpmnFactory.create("bpmn:UserTask");

    const shape = elementFactory.createShape({
      type: "bpmn:UserTask",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * Creates a function to start the creation of a BPMN Manual Task with a given suitability score.
   * @param {number} suitabilityScore - The suitability score to be assigned to the Manual Task.
   * @returns {Function} - A function that takes an event and starts the creation of a Manual Task.
   */
  function createManualTask(event) {
    const businessObject = bpmnFactory.create("bpmn:ManualTask");

    const shape = elementFactory.createShape({
      type: "bpmn:ManualTask",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * Creates a function to handle the 'Send Email' BPMN task.
   * @param {number} suitabilityScore - The score indicating the suitability of the task.
   * @returns {Function} A function that initializes and starts the creation of a BPMN SendTask element.
   * @param {Object} event - The event object triggering the task creation.
   */
  function createSendTask(event) {
    const businessObject = bpmnFactory.create("bpmn:SendTask");

    const shape = elementFactory.createShape({
      type: "bpmn:SendTask",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * Creates a function to start the creation of a BPMN Exclusive Gateway with a given suitability score.
   * @param {number} suitabilityScore - The suitability score to be assigned to the Exclusive Gateway.
   * @returns {Function} - A function that takes an event and starts the creation of an Exclusive Gateway.
   */
  function createExclusiveGateway(event) {
    const businessObject = bpmnFactory.create("bpmn:ExclusiveGateway");

    const shape = elementFactory.createShape({
      type: "bpmn:ExclusiveGateway",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * Creates a function to start the creation of a BPMN End Event with a given suitability score.
   * @param {number} suitabilityScore - The suitability score to be assigned to the End Event.
   * @returns {Function} - A function that takes an event and starts the creation of an End Event.
   */
  function createEndEvent(event) {
    const businessObject = bpmnFactory.create("bpmn:EndEvent");

    const shape = elementFactory.createShape({
      type: "bpmn:EndEvent",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * Creates a function to start the creation of a BPMN Terminate End Event with a given suitability score.
   * @param {number} suitabilityScore - The suitability score to be assigned to the Terminate End Event.
   * @returns {Function} - A function that takes an event and starts the creation of a Terminate End Event.
   */
  function createTerminateEndEvent(event) {
    const businessObject = bpmnFactory.create("bpmn:EndEvent");

    const shape = elementFactory.createShape({
      type: "bpmn:EndEvent",
      eventDefinitionType: "bpmn:TerminateEventDefinition",
      businessObject,
    });

    create.start(event, shape);
  }

  /**
   * REGISTRO DEL PROVIDER
   */
  palette.registerProvider({
    getPaletteEntries() {
      return {
        "tool.hand": {
          group: "tools",
          className: "bpmn-icon-hand-tool",
          title: i18n.t("bpmnLint.CustomPaletteHandTool"),
          action: {
            click: activateHandTool,
          },
        },

        "create.start-event": {
          group: "event",
          className: "bpmn-icon-start-event-none",
          title: i18n.t("bpmnLint.CustomPaletteCreateStartEvent"),
          action: {
            dragstart: createStartEvent,
            click: createStartEvent,
          },
        },

        "tool.global-connect": {
          group: "tools",
          className: "bpmn-icon-connection-multi",
          title: i18n.t("bpmnLint.CustomPaletteGlobalConnectTool"),
          action: {
            click: activateGlobalConnect,
          },
        },

        "create.user-task": {
          group: "activity",
          className: "bpmn-icon-user-task",
          title: i18n.t("bpmnLint.CustomPaletteCreateUserTask"),
          action: {
            dragstart: createUserTask,
            click: createUserTask,
          },
        },

        "create.manual-task": {
          group: "activity",
          className: "bpmn-icon-manual-task",
          title: i18n.t("bpmnLint.CustomPaletteCreateManualTask"),
          action: {
            dragstart: createManualTask,
            click: createManualTask,
          },
        },

        "create.send-task": {
          group: "activity",
          className: "bpmn-icon-send-task",
          title: i18n.t("bpmnLint.CustomPaletteCreateSendTask"),
          action: {
            dragstart: createSendTask,
            click: createSendTask,
          },
        },

        "create.exclusive-gateway": {
          group: "activity",
          className: "bpmn-icon-gateway-none",
          title: i18n.t("bpmnLint.CustomPaletteCreateExclusiveGateway"),
          action: {
            dragstart: createExclusiveGateway,
            click: createExclusiveGateway,
          },
        },

        "create.end-event": {
          group: "activity",
          className: "bpmn-icon-end-event-none",
          title: i18n.t("bpmnLint.CustomPaletteCreateEndEvent"),
          action: {
            dragstart: createEndEvent,
            click: createEndEvent,
          },
        },

        "create.terminate-end-event": {
          group: "activity",
          className: "bpmn-icon-end-event-terminate",
          title: i18n.t("bpmnLint.CustomPaletteCreateTerminationEndEvent"),
          action: {
            dragstart: createTerminateEndEvent,
            click: createTerminateEndEvent,
          },
        },
      };
    },
  });
}

CustomPaletteProvider.$inject = [
  "bpmnFactory",
  "create",
  "elementFactory",
  "palette",
  "handTool",
  "globalConnect",
];

const customPaletteModule = {
  __init__: ["paletteProvider"],
  paletteProvider: ["type", CustomPaletteProvider],
};

export default customPaletteModule;
